import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { cx } from '@platform/core/lib/cx';
import { CollapsibleGroup } from '@platform/engine/menu/CollapsibleGroup';
import { MenuHeader } from '@platform/engine/menu/MenuHeader';
import { useMenuOpenState } from '@platform/engine/menu/useMenuOpenState';
import { useAppSelector } from '@platform/state/hooks';
import { selectUi } from '@platform/state/selectors';
import type { IInboxMenuProps } from './IInboxMenuProps';
import { INBOX_SHOW, showCounts, workspaceCounts } from './inboxFilters';
import { useInbox } from './useInbox';
import { useInboxParams } from './useInboxParams';
import '@platform/engine/menu/menu.css';
import './inbox.css';

/**
 * Ліве меню «Моїх вхідних» (прототип v4 G:L293–310): «Особисте / Мої вхідні / N непрочитаних», «Показати» — усі, непрочитані, доручення,
 * нагадування, коментарі, оновлення з лічильниками; розгортна група «Робочі простори» — усі та кожен простір, з якого є елементи.
 */
export function InboxMenu({ ws }: IInboxMenuProps) {
  const { t } = useTranslation();
  const { data: me } = useMeQuery();
  const { entries, unread } = useInbox();
  const { show, space, setShow, setSpace } = useInboxParams();
  const openState = useMenuOpenState(`inbox:${ws}`);
  const collapsed = useAppSelector((s) => selectUi(s).leftMenuCollapsed);
  const counts = showCounts(entries);
  const item = (key: string, label: string, count: number, active: boolean, onClick: () => void) => (
    <button key={key || 'all'} type="button" className={cx('mnav__item ib-mitem', active && 'is-active')} aria-current={active ? 'true' : undefined} onClick={onClick}>
      <span className="mnav__label" title={label}>{label}</span><span className="mnav__count">{count}</span>
    </button>
  );
  const name = (id: string) => me?.workspaces.find((w) => w.id === id)?.name ?? id;
  return (
    <aside className={cx('po-menu mnav', collapsed && 'is-collapsed')} aria-label={t('inboxPage.title')}>
      <nav className="po-menu__nav mnav__nav" aria-label={t('inboxPage.title')}>
        <MenuHeader eyebrow={t('inboxPage.eyebrow')} title={t('inboxPage.title')} meta={t('inboxPage.unread', { count: unread })} />
        <div className="ib-mlabel">{t('inboxPage.show')}</div>
        {INBOX_SHOW.map((s) => item(s, t(`inboxPage.filters.${s}`), counts[s], show === s, () => setShow(s)))}
        <CollapsibleGroup title={t('inboxPage.workspaces')} open={openState.isOpen('workspaces', true)} onToggle={() => openState.toggle('workspaces', true)}>
          {item('', t('inboxPage.allWorkspaces'), entries.length, !space, () => setSpace(''))}
          {workspaceCounts(entries).map((w) => item(w.id, name(w.id), w.count, space === w.id, () => setSpace(w.id)))}
        </CollapsibleGroup>
      </nav>
    </aside>
  );
}
